import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Sparkles, Medal, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

function BonusRow({ label, points, status, hint, children }) {
    return (
        <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-start py-2 border-t border-[var(--border-muted)] first:border-t-0 first:pt-0">
            <div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-sm text-[var(--text-primary)]">{label}</div>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--accent-hex)] bg-[var(--accent-hex)]/15 px-1.5 py-0.5 rounded">{points}</span>
                    {status}
                </div>
                {hint && <div className="text-xs text-[var(--text-secondary)] mt-1">{hint}</div>}
                <div className="mt-2">{children}</div>
            </div>
        </div>
    );
}

function TeamSelect({ value, onChange, options, placeholder, testId, disabled }) {
    return (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            disabled={disabled}
            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)] disabled:opacity-50 disabled:cursor-not-allowed">
            <option value="">{placeholder}</option>
            {options.map((tm) => (
                <option key={tm.id} value={tm.id}>{tm.name}</option>
            ))}
        </select>
    );
}

export default function BonusTab({ poolId, teams, isAdmin, t, onChange }) {
    const [loading, setLoading] = useState(true);
    const [pred, setPred] = useState({
        top_scorer: "", gold_team_id: "", silver_team_id: "",
        bronze_team_id: "", most_goals_team_id: "", most_conceded_team_id: "",
    });
    const [points, setPoints] = useState({ total: 0, breakdown: [] });
    const [locked, setLocked] = useState(false);
    const [firstKickoff, setFirstKickoff] = useState(null);
    const [answers, setAnswers] = useState({
        top_scorer: "", gold_team_id: "", silver_team_id: "", bronze_team_id: "",
    });
    const [autoAgg, setAutoAgg] = useState({ most_goals_team_id: null, most_conceded_team_id: null });
    const [savingPred, setSavingPred] = useState(false);
    const [savingAns, setSavingAns] = useState(false);

    const teamOptions = useMemo(() => {
        return Object.values(teams || {})
            .filter((tm) => tm && tm.id)
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [teams]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [mineRes, ansRes] = await Promise.all([
                api.get(`/pools/${poolId}/bonus/me`),
                api.get(`/pools/${poolId}/bonus/answers`),
            ]);
            const mine = mineRes.data;
            setPred({
                top_scorer: mine.prediction?.top_scorer || "",
                gold_team_id: mine.prediction?.gold_team_id || "",
                silver_team_id: mine.prediction?.silver_team_id || "",
                bronze_team_id: mine.prediction?.bronze_team_id || "",
                most_goals_team_id: mine.prediction?.most_goals_team_id || "",
                most_conceded_team_id: mine.prediction?.most_conceded_team_id || "",
            });
            setPoints(mine.points || { total: 0, breakdown: [] });
            setLocked(!!mine.locked);
            setFirstKickoff(mine.first_kickoff || null);
            setAnswers({
                top_scorer: ansRes.data.answers?.top_scorer || "",
                gold_team_id: ansRes.data.answers?.gold_team_id || "",
                silver_team_id: ansRes.data.answers?.silver_team_id || "",
                bronze_team_id: ansRes.data.answers?.bronze_team_id || "",
            });
            setAutoAgg(ansRes.data.auto_aggregates || { most_goals_team_id: null, most_conceded_team_id: null });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [poolId]);

    useEffect(() => { reload(); }, [reload]);

    const savePred = async (e) => {
        e?.preventDefault?.();
        setSavingPred(true);
        try {
            const { data } = await api.put(`/pools/${poolId}/bonus/me`, pred);
            setPoints(data.points || { total: 0, breakdown: [] });
            toast.success(t("bonus.saved"));
            onChange?.();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSavingPred(false);
        }
    };

    const saveAnswers = async (e) => {
        e?.preventDefault?.();
        setSavingAns(true);
        try {
            await api.put(`/pools/${poolId}/bonus/answers`, answers);
            toast.success(t("bonus.answers.saved"));
            await reload();
            onChange?.();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSavingAns(false);
        }
    };

    const breakdownByKey = useMemo(() => {
        const map = {};
        for (const b of points.breakdown || []) map[b.key] = b;
        return map;
    }, [points]);

    const renderStatusBadge = (key) => {
        const b = breakdownByKey[key];
        if (!b) return null;
        if (b.status === "pending") {
            return <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-[var(--text-muted)]/15 text-[var(--text-muted)]">{t("bonus.pending_short")}</span>;
        }
        if (b.hit) {
            return <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-[var(--accent-hex)]/15 text-[var(--accent-hex)]">{t("bonus.hit_short")} (+{b.points})</span>;
        }
        return <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-red-500/15 text-red-400">{t("bonus.miss_short")}</span>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" /></div>
        );
    }

    const teamName = (tid) => teams?.[tid]?.name || (tid ? "—" : "");
    const tiedNames = (ids) => (ids || []).map(teamName).filter(Boolean).join(", ");

    return (
        <div data-testid="bonus-tab" className="space-y-6">
            <div>
                <h3 className="font-heading text-2xl font-bold uppercase text-[var(--text-primary)] flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-[var(--accent-hex)]" /> {t("bonus.title")}
                </h3>
                <p className="text-sm text-[var(--text-secondary)] mt-1">{t("bonus.subtitle")}</p>
            </div>

            {/* My picks */}
            {locked && (
                <div
                    data-testid="bonus-locked-banner"
                    className="flex items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm"
                >
                    <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-bold">{t("bonus.locked.title")}</p>
                        <p className="text-xs mt-0.5 text-[var(--text-secondary)]">
                            {t("bonus.locked.body", { date: firstKickoff ? new Date(firstKickoff).toLocaleString() : "—" })}
                        </p>
                    </div>
                </div>
            )}
            <form onSubmit={savePred} className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-5 space-y-4" data-testid="bonus-my-form">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h4 className="font-heading text-lg font-extrabold uppercase text-[var(--text-primary)]">{t("bonus.my.title")}</h4>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("bonus.points_earned")}</div>
                        <div className="font-heading text-2xl font-extrabold text-[var(--accent-hex)] tabular-nums" data-testid="bonus-my-points">{points.total} <span className="text-xs">/ 100</span></div>
                    </div>
                </div>

                {/* Top scorer */}
                <BonusRow label={t("bonus.row.top_scorer.label")} points={t("bonus.row.top_scorer.points")} status={renderStatusBadge("top_scorer")}>
                    <input value={pred.top_scorer} onChange={(e) => setPred({ ...pred, top_scorer: e.target.value })}
                        data-testid="bonus-input-top-scorer"
                        placeholder={t("bonus.row.top_scorer.ph")}
                        disabled={locked}
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)] disabled:opacity-50 disabled:cursor-not-allowed" />
                </BonusRow>

                {/* Gold/Silver/Bronze */}
                {[
                    { key: "gold_team_id", label: t("bonus.row.gold.label"), points: t("bonus.row.gold.points") },
                    { key: "silver_team_id", label: t("bonus.row.silver.label"), points: t("bonus.row.silver.points") },
                    { key: "bronze_team_id", label: t("bonus.row.bronze.label"), points: t("bonus.row.bronze.points") },
                ].map((row) => (
                    <BonusRow key={row.key} label={row.label} points={row.points} status={renderStatusBadge(row.key)}>
                        <TeamSelect value={pred[row.key]} onChange={(v) => setPred({ ...pred, [row.key]: v })}
                            options={teamOptions} placeholder={t("bonus.team.placeholder")}
                            disabled={locked}
                            testId={`bonus-select-${row.key}`} />
                    </BonusRow>
                ))}

                {/* Group-stage aggregates */}
                {[
                    { key: "most_goals_team_id", label: t("bonus.row.most_goals.label"), points: t("bonus.row.most_goals.points"), hint: t("bonus.row.most_goals.hint"), aggKey: "most_goals_team_id" },
                    { key: "most_conceded_team_id", label: t("bonus.row.most_conceded.label"), points: t("bonus.row.most_conceded.points"), hint: t("bonus.row.most_conceded.hint"), aggKey: "most_conceded_team_id" },
                ].map((row) => (
                    <BonusRow key={row.key} label={row.label} points={row.points} status={renderStatusBadge(row.key)} hint={
                        <>
                            {row.hint}
                            {autoAgg[row.aggKey] && (
                                <span className="ml-1 text-[var(--accent-hex)] font-semibold">{t("bonus.answers.auto_current")} {teamName(autoAgg[row.aggKey])}</span>
                            )}
                        </>
                    }>
                        <TeamSelect value={pred[row.key]} onChange={(v) => setPred({ ...pred, [row.key]: v })}
                            options={teamOptions} placeholder={t("bonus.team.placeholder")}
                            disabled={locked}
                            testId={`bonus-select-${row.key}`} />
                    </BonusRow>
                ))}

                <div className="pt-2 flex justify-end">
                    <button type="submit" disabled={savingPred || locked} data-testid="bonus-my-save"
                        className="bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-5 py-2.5 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50 disabled:cursor-not-allowed">
                        {savingPred ? t("bonus.saving") : t("bonus.save")}
                    </button>
                </div>
            </form>

            {/* Admin: top-scorer correct answer (free text — fuzzy matched server-side) */}
            {isAdmin && (
                <form onSubmit={saveAnswers} className="rounded-lg border border-[var(--accent-border)] bg-[var(--accent-hex)]/5 p-5 space-y-4" data-testid="bonus-answers-form">
                    <div>
                        <h4 className="font-heading text-lg font-extrabold uppercase text-[var(--accent-hex)] flex items-center gap-2">
                            <Medal className="h-5 w-5" /> {t("bonus.answers.title")}
                        </h4>
                        <p className="text-xs text-[var(--text-secondary)] mt-1">{t("bonus.answers.hint_top_scorer")}</p>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("bonus.row.top_scorer.label")}</label>
                        <input value={answers.top_scorer} onChange={(e) => setAnswers({ ...answers, top_scorer: e.target.value })}
                            data-testid="bonus-ans-top-scorer"
                            placeholder="e.g. Messi  —  or comma-separated for ties: Messi, Haaland"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]" />
                    </div>
                    <div className="pt-2 flex justify-end">
                        <button type="submit" disabled={savingAns} data-testid="bonus-ans-save"
                            className="bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-5 py-2.5 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50">
                            {savingAns ? t("bonus.saving") : t("bonus.answers.save")}
                        </button>
                    </div>
                </form>
            )}

            {/* Resolved correct answers — visible to ALL members (read-only).
                Auto-derived from finished knockout matches (Gold/Silver/Bronze)
                and finished group-stage matches (Most goals / Most conceded — ties listed). */}
            <div className="rounded-lg border border-[var(--accent-border)] bg-[var(--accent-hex)]/5 p-5 space-y-3" data-testid="bonus-resolved-card">
                <h4 className="font-heading text-lg font-extrabold uppercase text-[var(--accent-hex)] flex items-center gap-2">
                    <Medal className="h-5 w-5" /> {t("bonus.resolved.title")}
                </h4>
                <p className="text-xs text-[var(--text-secondary)]">{t("bonus.resolved.hint")}</p>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    <div className="rounded-md border border-[var(--border-muted)] bg-[var(--bg-base)] p-3" data-testid="resolved-top-scorer">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("bonus.row.top_scorer.label")}</div>
                        {(() => {
                            const raw = (answers.top_scorer || "").trim();
                            if (!raw) return <div className="text-[var(--text-primary)] mt-1 font-bold">{t("bonus.answers.not_set")}</div>;
                            const names = raw.split(/[,;\n/|]+/).map((s) => s.trim()).filter(Boolean);
                            if (names.length <= 1) return <div className="text-[var(--text-primary)] mt-1 font-bold">{raw}</div>;
                            return (
                                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="resolved-top-scorer-list">
                                    {names.map((n, i) => (
                                        <span key={`${n}-${i}`} className="text-[var(--text-primary)] font-bold bg-[var(--accent-hex)]/10 border border-[var(--accent-border)] px-2 py-0.5 rounded text-xs">{n}</span>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                    {[
                        { key: "gold_team_id", label: t("bonus.row.gold.label") },
                        { key: "silver_team_id", label: t("bonus.row.silver.label") },
                        { key: "bronze_team_id", label: t("bonus.row.bronze.label") },
                    ].map((row) => (
                        <div key={row.key} className="rounded-md border border-[var(--border-muted)] bg-[var(--bg-base)] p-3" data-testid={`resolved-${row.key}`}>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{row.label}</div>
                            <div className="text-[var(--text-primary)] mt-1 font-bold">{teamName(autoAgg[row.key] || answers[row.key]) || t("bonus.answers.not_set")}</div>
                        </div>
                    ))}
                    <div className="rounded-md border border-[var(--border-muted)] bg-[var(--bg-base)] p-3" data-testid="resolved-most-goals">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("bonus.row.most_goals.label")}</div>
                        <div className="text-[var(--text-primary)] mt-1 font-bold">{tiedNames(autoAgg.most_goals_team_ids) || t("bonus.answers.not_set")}</div>
                    </div>
                    <div className="rounded-md border border-[var(--border-muted)] bg-[var(--bg-base)] p-3" data-testid="resolved-most-conceded">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("bonus.row.most_conceded.label")}</div>
                        <div className="text-[var(--text-primary)] mt-1 font-bold">{tiedNames(autoAgg.most_conceded_team_ids) || t("bonus.answers.not_set")}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
